// Fog-reveal math — pure helpers (practice-loop-v2, topic-fog-reveal spec).

import type { PackId } from '@skena/schema'

/**
 * Deterministic qid → tile mapping: tiles are indexed by the qid's position
 * in the sorted list of the topic's question ids. Stable across renders,
 * sessions, and devices (spec: Deterministic mapping).
 *
 * `allQids` is the union of the server's correctQids and the round's queue
 * qids for the target topic; callers pass the topic's full qid list when
 * they have it. Unknown qids map to -1 (no tile).
 */
export function tileIndexForQid(allQids: readonly string[], qid: string): number {
  return [...allQids].sort().indexOf(qid)
}

/** Set of cleared tile indexes for the current progress state. */
export function clearedTileIndexes(
  allQids: readonly string[],
  correctQids: ReadonlySet<string>,
): ReadonlySet<number> {
  const sorted = [...allQids].sort()
  const cleared = new Set<number>()
  sorted.forEach((qid, idx) => {
    if (correctQids.has(qid)) cleared.add(idx)
  })
  return cleared
}

/**
 * Topic id → item id by the seed naming convention (`{packId}-{itemId}`,
 * e.g. joseph-coat → coat, nativity-baby-jesus → baby-jesus). The fog
 * banner uses this to locate the item's lineart thumb; the <img> onError
 * fallback covers any topic that ever breaks the convention.
 */
export function itemIdForTopic(packId: PackId, topicId: string): string {
  const prefix = `${packId}-`
  return topicId.startsWith(prefix) ? topicId.slice(prefix.length) : topicId
}
